import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, Music, Pen, Play, X, ExternalLink } from "lucide-react";
import { bioData, featuredProjects } from "../../data/mockData";

const AboutSection = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const disciplines = [
    { icon: Camera, label: "Film", description: "Independent films & documentaries" },
    { icon: Music, label: "Music", description: "Music-related projects & videos" },
    { icon: Pen, label: "Writing", description: "Storytelling & creative writing" },
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-32 overflow-hidden"
      style={{ background: "#0f0f0f" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Label */}
        <div className={`mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p
            className="text-[#c8a55c] text-sm tracking-[0.3em] uppercase mb-4"
            style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600 }}
          >
            About
          </p>
          <h2
            className="text-[#f5f0e8] text-5xl sm:text-6xl"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            The Story Behind the Lens
          </h2>
        </div>

        {/* Bio + Image Grid */}
        <div className="grid lg:grid-cols-5 gap-16 items-start">
          {/* Bio Text */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="space-y-6">
              {bioData.paragraphs.map((para, i) => (
                <p
                  key={i}
                  className="text-[#d0c8b8] text-lg leading-[1.85]"
                  style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                >
                  {para}
                </p>
              ))}
            </div>

            {/* Disciplines */}
            <div className="mt-14 grid sm:grid-cols-3 gap-6">
              {disciplines.map((d, i) => (
                <div
                  key={i}
                  className="group p-6 border border-[#1f1f1f] hover:border-[#c8a55c]/20 transition-all duration-500 bg-[#0a0a0a]/50"
                >
                  <d.icon className="w-5 h-5 text-[#c8a55c] mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h4
                    className="text-[#f5f0e8] text-xl mb-2"
                    style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
                  >
                    {d.label}
                  </h4>
                  <p
                    className="text-[#9a9088] text-sm"
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                  >
                    {d.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* 1980s London Photo */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="relative group">
              <img
                src={bioData.images.london1980s}
                alt="Steve Sattler in London, 1980s - Listen to London"
                className="w-full aspect-[3/4] object-cover object-top"
              />
              {/* Overlay caption */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <p
                  className="text-[#c8a55c] text-2xl"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}
                >
                  London, 1980s
                </p>
                <p
                  className="text-[#8a8070] text-sm mt-1"
                  style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                >
                  Filming the Emmy award-winning "Listen to London"
                </p>
              </div>
              {/* Frame accent */}
              <div className="absolute inset-0 border border-[#c8a55c]/10 pointer-events-none group-hover:border-[#c8a55c]/20 transition-colors duration-500" />
            </div>

            {/* Cameraman caricature */}
            <div className="mt-8 relative group">
              <img
                src={bioData.images.caricatureCamera}
                alt="Steve Sattler - Cameraman Caricature"
                className="w-full aspect-square object-cover rounded-sm"
              />
              <div className="absolute inset-0 border border-[#c8a55c]/10 pointer-events-none group-hover:border-[#c8a55c]/20 transition-colors duration-500" />
            </div>
          </div>
        </div>

        {/* Featured Projects */}
        <div id="projects" className={`mt-32 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p
            className="text-[#c8a55c] text-sm tracking-[0.3em] uppercase mb-4"
            style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600 }}
          >
            Featured Projects
          </p>
          <h2
            className="text-[#f5f0e8] text-5xl sm:text-6xl mb-14"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            Signature Works
          </h2>

          {/* Listen to London — Full-width feature */}
          <div className="mb-12 border border-[#1f1f1f] hover:border-[#c8a55c]/20 transition-all duration-500 overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Clickable video thumbnail */}
              <div
                className="relative aspect-video lg:aspect-auto lg:min-h-[420px] bg-[#0a0a0a] cursor-pointer group/play overflow-hidden"
                onClick={() => setPlayingVideo({ id: "w5hTNb6zd5w", title: "Listen to London" })}
              >
                <img
                  src="https://img.youtube.com/vi/w5hTNb6zd5w/maxresdefault.jpg"
                  alt="Listen to London — Steve Sattler Films"
                  className="absolute inset-0 w-full h-full object-cover brightness-75 group-hover/play:brightness-50 group-hover/play:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full border-2 border-[#c8a55c] flex items-center justify-center bg-[#0a0a0a]/40 backdrop-blur-sm group-hover/play:bg-[#c8a55c]/20 transition-all duration-300 group-hover/play:scale-110">
                    <Play className="w-8 h-8 text-[#c8a55c] ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4">
                  <span
                    className="text-[#f5f0e8]/60 text-xs tracking-[0.1em] uppercase"
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
                  >
                    Play Video
                  </span>
                </div>
              </div>
              {/* Project details */}
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="text-[#c8a55c] text-xs tracking-[0.15em] uppercase px-3 py-1 border border-[#c8a55c]/20"
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600 }}
                  >
                    Featured Project
                  </span>
                  <span
                    className="text-[#8a8070] text-xs"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    1980s
                  </span>
                </div>
                <h3
                  className="text-[#f5f0e8] text-4xl sm:text-5xl mb-5"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
                >
                  Listen to London
                </h3>
                <p
                  className="text-[#d0c8b8] text-base leading-[1.8] mb-6"
                  style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                >
                  An Emmy award-winning project filmed on the streets of London, capturing the pulse and spirit of the city through an authentic lens. This landmark work defined Steve's commitment to honesty in storytelling — immersing audiences in the sights, sounds, and soul of a city at a pivotal cultural moment.
                </p>
                <div className="flex items-center gap-4">
                  <img
                    src="/images/london1980s.jpg"
                    alt="Steve in London, 1980s"
                    className="w-14 h-14 object-cover object-top border border-[#c8a55c]/20"
                  />
                  <div>
                    <p
                      className="text-[#f5f0e8] text-sm"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
                    >
                      Steve Sattler — London, 1980s
                    </p>
                    <p
                      className="text-[#6a6058] text-xs mt-0.5"
                      style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                    >
                      Emmy Award-Winning Production
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* My Country — Atmospheric teaser */}
          <div className="border border-[#1f1f1f] hover:border-[#c8a55c]/20 transition-all duration-500 overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Atmospheric visual */}
              <div className="relative min-h-[300px] lg:min-h-[380px] bg-[#0c0c0c] flex items-center justify-center overflow-hidden">
                {/* Layered typography background */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    className="text-[#c8a55c]/[0.04] text-[180px] sm:text-[220px] leading-none select-none"
                    style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                  >
                    2025
                  </span>
                </div>
                <div className="relative z-10 text-center px-8">
                  <p
                    className="text-[#c8a55c]/60 text-sm tracking-[0.4em] uppercase mb-4"
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600 }}
                  >
                    Coming Soon
                  </p>
                  <h4
                    className="text-[#f5f0e8] text-5xl sm:text-6xl mb-4"
                    style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                  >
                    My Country
                  </h4>
                  <div className="w-12 h-px bg-[#c8a55c]/30 mx-auto" />
                </div>
              </div>
              {/* Project details */}
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="text-[#c8a55c] text-xs tracking-[0.15em] uppercase px-3 py-1 border border-[#c8a55c]/20"
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600 }}
                  >
                    Upcoming Project
                  </span>
                  <span
                    className="text-[#8a8070] text-xs"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    2025
                  </span>
                </div>
                <h3
                  className="text-[#f5f0e8] text-4xl sm:text-5xl mb-5"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
                >
                  My Country
                </h3>
                <p
                  className="text-[#d0c8b8] text-base leading-[1.8] mb-6"
                  style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                >
                  A deeply personal exploration of place, belonging, and the landscapes that shape who we are. Continuing Steve's lifelong investigation of identity and memory, this project turns the camera toward the terrain of home — asking what it means to truly know a place and be known by it.
                </p>
                <blockquote className="border-l-2 border-[#c8a55c]/30 pl-5">
                  <p
                    className="text-[#c8a55c]/70 text-sm italic leading-relaxed"
                    style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 300 }}
                  >
                    "Every landscape holds a story. Every story holds a truth about who we are."
                  </p>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {playingVideo && (
        <VideoModal video={playingVideo} onClose={() => setPlayingVideo(null)} />
      )}
    </section>
  );
};

const VideoModal = ({ video, onClose }) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      window.scrollTo(0, scrollY);
    };
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 sm:p-8"
      onClick={onClose}
    >
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-[#8a8070] hover:text-[#c8a55c] transition-colors duration-300 flex items-center gap-2"
        >
          <span
            className="text-sm tracking-[0.1em] uppercase"
            style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 500 }}
          >
            Close
          </span>
          <X className="w-5 h-5" />
        </button>
        <div className="relative aspect-video bg-[#0a0a0a] border border-[#c8a55c]/10">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <h3
            className="text-[#f5f0e8] text-2xl"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
          >
            {video.title}
          </h3>
        </div>
      </div>
    </div>
  );
};

export default AboutSection;
